import {decode} from 'html-entities';
import {filterEmpty} from './array';
import {isArray, isEmpty} from './boolean';
import {
  formUtcToLocal,
  dateTimeToDisplayLongFormat,
  dateToFormat,
} from './date';
import {toNumber} from './number';
import {getObjectProp} from './object';
import {toString} from './string';

/**
 *
 * @param {number} num
 * @param {number} from
 * @param {number} to
 * @returns {boolean}
 */
const filterNumberBetween = (num, from, to) => {
  if (num < 0) {
    return false;
  }
  if (from && to) {
    return num >= from && num <= to;
  }
  if (from && !to) {
    return num >= from;
  }
  if (!from && to) {
    return num <= to;
  }
  return true;
};

/**
 *
 * @param {object} dates
 * @returns {array}
 */
export const flattenPropertyViewingsToLocal = dates => {
  const values = Object.values(dates);
  const flattenedValues = [].concat(...values);
  return flattenedValues.map(value => ({
    dateTime: value.date_time,
    id: value.id,
    localDateTime: formUtcToLocal(value.date_time),
    localEndDateTime: formUtcToLocal(value.end_date_time),
    totalAllowed: toNumber(value.total_allowed),
    totalBookingsAllowed: toNumber(value.total_bookings_allowed),
    totalBookings: toNumber(value.attending_count),
    actualBookingCount: toNumber(value.booking_count),
    maxViewersType: value.viewing_max_viewers_type,
  }));
};

/**
 *
 * @param {array} viewings
 * @returns {array}
 */
export const preparePropertyViewings = viewings =>
  viewings
    .map(viewing => {
      const start = dateTimeToDisplayLongFormat(
        viewing.localDateTime,
        'DD/MM/YYYY, h:mm:ss a'
      );
      const end = dateTimeToDisplayLongFormat(
        viewing.localEndDateTime,
        'DD/MM/YYYY, h:mm:ss a',
        'h:mma'
      );
      return {
        ...viewing,
        label: filterEmpty([start, end]).join(' - '),
        value: toString(viewing.id),
      };
    })
    .filter(viewing => {
      let allowed = true;

      // Allow fall through for maxViewersType === MAX_ATTENDEES_AND_BOOKINGS

      if (viewing.maxViewersType !== 'MAX_ATTENDEES') {
        // eslint-disable-next-line fp/no-mutation
        allowed =
          allowed && viewing.actualBookingCount < viewing.totalBookingsAllowed;
      }

      if (viewing.maxViewersType !== 'MAX_BOOKINGS') {
        // eslint-disable-next-line fp/no-mutation
        allowed = allowed && viewing.totalBookings < viewing.totalAllowed;
      }

      return allowed;
    });

/**
 *
 * @param {object} data
 * @returns {array}
 */
export const prepareLocalViewings = data => {
  const formatted = flattenPropertyViewingsToLocal(data);
  return preparePropertyViewings(formatted);
};

/**
 *
 * @param {object} property
 * @returns {object}
 */
export const preparePropertyProps = property => {
  const images = getObjectProp(property, 'images') || [];
  const dateAvailable = getObjectProp(property, 'available_at');
  const dateAvailableDisplay = dateToFormat(dateAvailable);
  const rentAmount = toNumber(getObjectProp(property, 'rent_amount', 0), 0);
  const rentFrequency = getObjectProp(property, 'rental_period').trim();
  const latitude = getObjectProp(property, 'propertyFeature.latitude');
  const longitude = getObjectProp(property, 'propertyFeature.longitude');
  const city = getObjectProp(property, 'city');
  const suburb = getObjectProp(property, 'suburb');
  const postcode = getObjectProp(property, 'postcode');

  return {
    address: getObjectProp(property, 'address'),
    id: getObjectProp(property, 'id'),
    unit: getObjectProp(property, 'unit'),
    streetNumber: getObjectProp(property, 'street_number'),
    streetName: getObjectProp(property, 'street_name'),
    city,
    suburb,
    dwellingType: getObjectProp(property, 'dwelling_type'),
    thumbphoto: images.length > 0 ? images[0].thumbnail_url : undefined,
    mediumphoto: images.length > 0 ? images[0].medium_url : undefined,
    photo: images.length > 0 ? images[0].large_url : undefined,
    bedrooms: toNumber(getObjectProp(property, 'num_bedrooms', 0)),
    bathrooms: toNumber(getObjectProp(property, 'num_bathrooms', 0)),
    parkingSpaces: toNumber(getObjectProp(property, 'num_cars', 0)),
    rentAmount,
    rentFrequency,
    status: getObjectProp(property, 'status'),
    description: decode(getObjectProp(property, 'propertyFeature.description')),
    latitude: latitude ? latitude * 1 : null,
    longitude: longitude ? longitude * 1 : null,
    addressPart1: getObjectProp(property, 'short_address'),
    addressPart2: [suburb, city, postcode].filter(Boolean).join(', '),
    postcode,
    dateAvailable,
    dateAvailableDisplay: dateAvailableDisplay || '',
    rent: ['$', rentAmount, ` ${rentFrequency}`]
      .filter((v, _, items) => {
        if (!items[1]) {
          return false;
        }
        return Boolean(v);
      })
      .join(''),
  };
};

/**
 *
 * @param {array} images
 * @returns {array}
 */
export const preparePropertyImages = images => {
  if (!isArray(images)) {
    return [];
  }
  return images.map(image => ({
    id: getObjectProp(image, 'id'),
    name: getObjectProp(image, 'nice_name'),
    src: getObjectProp(image, 'large_url'),
    thumbSrc: getObjectProp(image, 'thumbnail_url'),
  }));
};

/**
 *
 * @param {array} items
 * @returns {array}
 */
export const preparePropertyListProps = items => {
  if (!isArray(items)) {
    return [];
  }
  return items.map(item => preparePropertyProps(item));
};

/**
 *
 * @param {array} items
 * @param {object} filters
 * @returns {array}
 */
export const filterProperties = (items, filters) => {
  if (isEmpty(filters)) {
    return items;
  }
  const query = filters.query
    ? toString(filters.query).trim().toLowerCase()
    : '';
  return items.filter(
    item =>
      (query ? item.address.toLowerCase().includes(query) : true) &&
      filterNumberBetween(
        item.bedrooms,
        filters.bedrooms_from,
        filters.bedrooms_to
      ) &&
      filterNumberBetween(item.rentAmount, filters.rent_from, filters.rent_to)
  );
};
